"use client"

import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Upload, Image as ImageIcon, X, FileText, Eye, Code } from 'lucide-react';
import SignatureService from '@/api/signature.service';
import { Radio, RadioGroup, RadioIndicator, RadioItem } from '@/components/ui/radio';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TiptapEditor } from '@/components/ui/tiptap-editor';
import { HtmlPreview } from '@/components/ui/html-preview';

interface CreateSignatureFormProps {
  businessId: number;
  signatureId?: string;
  initialData?: any;
  onSuccess: () => void;
}

export default function CreateSignatureForm({ 
  businessId, 
  signatureId, 
  initialData, 
  onSuccess 
}: CreateSignatureFormProps) {
  const [name, setName] = useState('');
  const [content, setContent] = useState('');
  const [editorMode, setEditorMode] = useState<'visual' | 'html'>('visual');
  const [signatureType, setSignatureType] = useState<'text' | 'image' | 'both'>('text');
  const [isDefault, setIsDefault] = useState(false);
  const [includeContactInfo, setIncludeContactInfo] = useState(true);
  const [includeDisclaimer, setIncludeDisclaimer] = useState(false);
  const [disclaimer, setDisclaimer] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showUploadProgress, setShowUploadProgress] = useState(false);
  const [previewImage, setPreviewImage] = useState<{url: string, name: string} | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  
  const [selectedBusiness] = useState(() => {
    const storedBusiness = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBusiness') : null;
    return storedBusiness ? JSON.parse(storedBusiness) : null;
  });

  const isEditMode = !!signatureId;
  
  // Load initial data if in edit mode
  useEffect(() => {
    if (isEditMode && initialData) {
      setName(initialData.name || '');
      setContent(initialData.content || '');
      setSignatureType(initialData.signatureType || 'text');
      setIsDefault(initialData.isDefault || false);
      setIncludeContactInfo(initialData.includeContactInfo !== undefined ? initialData.includeContactInfo : true);
      setIncludeDisclaimer(initialData.includeDisclaimer || false);
      setDisclaimer(initialData.disclaimer || '');
      
      if (initialData.imageLink) {
        setExistingImageUrl(initialData.imageLink);
      }
    }
  }, [isEditMode, initialData]);
  
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      if (file.type.startsWith('image/')) {
        setImageFile(file);
        
        // Create preview URL
        const previewUrl = URL.createObjectURL(file);
        setPreviewImage({
          url: previewUrl,
          name: file.name
        });
      } else {
        toast({
          title: "Invalid file type",
          description: "Please select an image file (JPG, PNG)",
          variant: "destructive",
        });
      }
    }
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const removeImage = () => {
    setImageFile(null);
    setExistingImageUrl(null);
    if (previewImage) {
      URL.revokeObjectURL(previewImage.url);
      setPreviewImage(null);
    }
  };
  
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    else return (bytes / 1048576).toFixed(1) + ' MB';
  };
  
  const handlePreview = () => {
    setShowPreview(true);
  };
  
  const validateForm = () => {
    if (!name) {
      toast({
        title: "Validation Error",
        description: "Signature name is required",
        variant: "destructive",
      });
      return false;
    }
    
    if (signatureType === 'text' || signatureType === 'both') {
      if (!content) {
        toast({
          title: "Validation Error",
          description: "Signature content is required for text-based signatures",
          variant: "destructive",
        });
        return false;
      }
    }
    
    if ((signatureType === 'image' || signatureType === 'both') && !imageFile && !existingImageUrl) {
      toast({
        title: "Validation Error",
        description: "An image is required for image-based signatures",
        variant: "destructive",
      });
      return false;
    }
    
    if (includeDisclaimer && !disclaimer) {
      toast({
        title: "Validation Error",
        description: "Disclaimer text is required when including a disclaimer",
        variant: "destructive",
      });
      return false;
    }
    
    return true;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    setShowUploadProgress(!!imageFile);
    setUploadProgress(0);
    
    try {
      // Use FormData if there's an image
      let signatureData: any;
      let response;
      
      if (imageFile) {
        signatureData = new FormData();
        signatureData.append('name', name);
        signatureData.append('businessId', selectedBusiness.id.toString());
        signatureData.append('signatureType', signatureType);
        signatureData.append('isDefault', isDefault.toString());
        signatureData.append('includeContactInfo', includeContactInfo.toString());
        signatureData.append('includeDisclaimer', includeDisclaimer.toString());
        
        if (content) {
          signatureData.append('content', content);
        }
        
        if (includeDisclaimer && disclaimer) {
          signatureData.append('disclaimer', disclaimer);
        }
        
        // Add business ID
        signatureData.append('businessId', businessId.toString());
        
        // Add image
        signatureData.append('image', imageFile);
        
        // Edit or create
        if (isEditMode) {
          response = await SignatureService.updateSignature(signatureId, signatureData, progressEvent => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          });
        } else {
          response = await SignatureService.createSignature(signatureData, progressEvent => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          });
        }
      } else {
        // Use regular JSON if no image upload
        signatureData = {
          name,
          signatureType,
          content: signatureType === 'text' || signatureType === 'both' ? content : '',
          isDefault,
          includeContactInfo,
          includeDisclaimer,
          disclaimer: includeDisclaimer ? disclaimer : '',
          businessId,
        };
        
        // Edit or create
        if (isEditMode) {
          response = await SignatureService.updateSignature(signatureId, signatureData);
        } else {
          response = await SignatureService.createSignature(signatureData);
        }
      }
      
      if (response.data.result === 'success') {
        toast({
          title: "Success",
          description: isEditMode ? "Signature updated successfully" : "Signature created successfully",
        });
        onSuccess();
      } else {
        toast({
          title: "Error",
          description: response.data.message || (isEditMode ? "Failed to update signature" : "Failed to create signature"),
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error saving signature:", error);
      toast({
        title: "Error",
        description: "An error occurred while saving the signature",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
      setShowUploadProgress(false);
    }
  };
  
  // Clean up object URLs when component unmounts or when previewImage changes
  useEffect(() => {
    return () => {
      if (previewImage) {
        URL.revokeObjectURL(previewImage.url);
      }
    };
  }, [previewImage]);
  
  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Signature Name */}
      <div className="space-y-2">
        <Label htmlFor="name">Signature Name</Label>
        <Input
          id="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Professional Signature"
          required
        />
      </div>
      
      {/* Signature Type */}
      <div className="space-y-2">
        <Label>Signature Type</Label>
        <RadioGroup value={signatureType} onValueChange={(value) => setSignatureType(value as 'text' | 'image' | 'both')}>
          <div className="flex flex-col gap-3">
            <div className="flex items-center space-x-2">
              <RadioItem value="text" id="signature-text">
                <RadioIndicator />
              </RadioItem>
              <Label htmlFor="signature-text" className="cursor-pointer">Text only</Label>
            </div>
            
            <div className="flex items-center space-x-2">
              <RadioItem value="image" id="signature-image">
                <RadioIndicator />
              </RadioItem>
              <Label htmlFor="signature-image" className="cursor-pointer">Image only</Label>
            </div>
            
            <div className="flex items-center space-x-2">
              <RadioItem value="both" id="signature-both">
                <RadioIndicator />
              </RadioItem>
              <Label htmlFor="signature-both" className="cursor-pointer">Text and Image</Label>
            </div>
          </div>
        </RadioGroup>
      </div>
      
      {/* Text Content */}
      {(signatureType === 'text' || signatureType === 'both') && (
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <Label htmlFor="content">Signature Content</Label>
            <Tabs value={editorMode} onValueChange={(value) => setEditorMode(value as 'visual' | 'html')}>
              <TabsList className="grid w-[200px] grid-cols-2">
                <TabsTrigger value="visual">Visual</TabsTrigger>
                <TabsTrigger value="html">HTML</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          
          {editorMode === 'visual' ? (
            <div className="space-y-4">
              <TiptapEditor 
                content={content} 
                onChange={setContent}
                placeholder="Create your signature..."
              />
              <p className="text-xs text-muted-foreground">
                Use the toolbar to format your text, add links, and insert images.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="bg-amber-50 border border-amber-200 p-2 rounded-md mb-2">
                <p className="text-xs text-amber-700">
                  <strong>Note:</strong> You are editing raw HTML. Switch to Visual mode for a simpler editing experience.
                </p>
              </div>
              <Textarea
                id="content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Enter HTML content for your signature"
                className="min-h-32 font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                You can use HTML to format your signature. Use &lt;br&gt; for line breaks, &lt;a href="..."&gt; for links, etc.
              </p>
            </div>
          )}
          
          <div className="mt-4 border-t pt-4">
            <h4 className="text-sm font-medium mb-2">Preview</h4>
            <HtmlPreview html={content} className="min-h-[100px]" />
          </div>
        </div>
      )}
      
      {/* Image Upload */}
      {(signatureType === 'image' || signatureType === 'both') && (
        <div className="space-y-3">
          <Label>Signature Image</Label>
          
          {(imageFile || existingImageUrl) ? (
            <div className="border rounded-md p-3 space-y-2">
              <div className="flex items-center justify-between bg-muted/30 p-2 rounded-md">
                <div className="flex items-center gap-2">
                  <ImageIcon className="w-4 h-4 text-green-500" />
                  <div className="text-sm truncate max-w-[200px]">
                    {imageFile ? imageFile.name : existingImageUrl?.split('/').pop()}
                  </div>
                  {imageFile && <div className="text-xs text-muted-foreground">{formatFileSize(imageFile.size)}</div>}
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handlePreview} 
                    className="h-6 px-2 py-0 text-xs"
                  >
                    <Eye className="h-3 w-3 mr-1" />
                    Preview
                  </Button>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={removeImage} 
                  className="h-6 w-6 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => fileInputRef.current?.click()}
                className="gap-1"
              >
                <Upload className="h-4 w-4" />
                Upload Image
              </Button>
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept="image/png,image/jpeg,image/jpg"
                onChange={handleFileChange}
              />
              <p className="text-xs text-muted-foreground">
                Supported formats: JPEG, PNG. Max size: 5MB.
              </p>
            </div>
          )}
        </div>
      )}
      
      {/* Options */}
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <Checkbox 
            id="isDefault" 
            checked={isDefault} 
            onCheckedChange={(checked) => setIsDefault(!!checked)} 
          />
          <Label htmlFor="isDefault" className="cursor-pointer">Set as default signature</Label>
        </div>
        
        <div className="flex items-center space-x-2">
          <Checkbox 
            id="includeContactInfo" 
            checked={includeContactInfo} 
            onCheckedChange={(checked) => setIncludeContactInfo(!!checked)} 
          />
          <Label htmlFor="includeContactInfo" className="cursor-pointer">Include business contact information</Label>
        </div>
        
        <div className="flex items-center space-x-2">
          <Checkbox 
            id="includeDisclaimer" 
            checked={includeDisclaimer} 
            onCheckedChange={(checked) => setIncludeDisclaimer(!!checked)} 
          />
          <Label htmlFor="includeDisclaimer" className="cursor-pointer">Include disclaimer</Label>
        </div>
      </div>
      
      {/* Disclaimer */}
      {includeDisclaimer && (
        <div className="space-y-2">
          <Label htmlFor="disclaimer">Disclaimer Text</Label>
          <Textarea
            id="disclaimer"
            value={disclaimer}
            onChange={(e) => setDisclaimer(e.target.value)}
            placeholder="Enter your disclaimer text"
            className="h-20"
          />
        </div>
      )}
      
      {/* Upload Progress */}
      {showUploadProgress && (
        <div className="space-y-1">
          <div className="text-sm flex justify-between">
            <span>Uploading image</span>
            <span>{uploadProgress}%</span>
          </div>
          <Progress value={uploadProgress} />
        </div>
      )}
      
      {/* Form Actions */}
      <div className="flex justify-end">
        <Button type="submit" disabled={isLoading}>
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {isEditMode ? 'Update Signature' : 'Create Signature'}
        </Button>
      </div>
      
      {/* Image Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-3xl">
          <DialogTitle className="mb-4">Image Preview</DialogTitle>
          <div className="relative overflow-hidden rounded-md">
            <img
              src={previewImage?.url || existingImageUrl || ''}
              alt="Signature preview"
              className="w-full h-auto object-contain"
            />
          </div>
        </DialogContent>
      </Dialog>
    </form>
  );
} 